import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ElementRef, Renderer2, Inject, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { UnreconciledReportService } from 'src/app/data/service/UnreconciledReport.service';
import { OrganizationService } from 'src/app/data/service/organization.service';

@Component({
  selector: 'app-ai-assistant',
  templateUrl: './ai-assistant.component.html',
  styleUrls: ['./ai-assistant.component.scss']
})
export class AiAssistantComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() reportName: string = 'Report';
  @Input() status: string | null = null; // e.g., Sent For Review, Complete, Draft
  // When true, the panel docks inside the parent container (no global overlay)
  @Input() dockMode: boolean = false;
  // How to promote the assistant when closed: 'toast' (default) or 'card'
  @Input() promoStyle: 'toast' | 'card' = 'toast';
  // Only show the promo (toast/card) when the parent indicates data is ready
  @Input() showPromo: boolean = true;
  @Input() currentReportStatus: string = '';
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() callAssistant = new EventEmitter<void>();
  @Output() openApproverModalEvt = new EventEmitter<void>();
  @Input() isApprovalMode: boolean = false;
  // Emit when a suggestion card is selected; payload is array of failedIds (strings)
  @Output() suggestionSelected = new EventEmitter<string[]>();
  // Emit when a suggestion/check is approved or unapproved. Payload: { name: string, approved: boolean }
  @Output() suggestionApproved = new EventEmitter<{ id: string; approved: boolean }>();

  // When user dismisses the promo for the current session
  promoDismissed: boolean = false;

  // Dummy insights data
  summaryText = '';
  messageText = '';
  messages: Array<{ from: 'bot' | 'user'; text: string }> = [];
  highlights: string[] = [];

  // Charts (ng2-charts v2 + chart.js v2)
  lineChartData: any[] = [
    { data: [12, 14, 10, 16, 18, 22, 21, 24, 26, 25, 29, 31], label: 'Reports created' }
  ];
  lineChartLabels: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  lineChartOptions: any = { responsive: true, maintainAspectRatio: false, legend: { display: false } };
  lineChartColors: any[] = [{ borderColor: '#157AF3', backgroundColor: 'rgba(21,122,243,0.1)' }];
  lineChartType: any = 'line';

  docsChartData: number[] = [76, 24]; // with docs, without
  docsChartLabels: string[] = ['With docs', 'Missing docs'];
  docsChartColors: any[] = [{ backgroundColor: ['#34C759', '#FFC107'] }];
  docsChartType: any = 'doughnut';
  docsChartOptions: any = { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' } };

  weekdayVsWeekendData: any[] = [
    { data: [42000, 16000], label: 'Spend' }
  ];
  weekdayVsWeekendLabels: string[] = ['Weekdays', 'Weekends'];
  weekdayVsWeekendType: any = 'bar';
  weekdayVsWeekendOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    scales: { yAxes: [{ ticks: { callback: (v: number) => `$${v.toLocaleString()}` } }] }
  };

  showMoreInsights = false;
  // Start with summarized view by default; if true, expand all charts initially
  @Input() showCharts: boolean = false;
  // Optional AI response payload wired from the parent page
  @Input() aiResponse: any = null;
  // When true, display loading state (parent should toggle while calling API)
  @Input() loading: boolean = false;
  // Container for playing lottie animation when loading
  @ViewChild('lottieContainer', { static: false }) lottieContainer?: ElementRef;

  private _lottiePlayer: any = null;
  // expose for template binding
  public _lottieLoaded: boolean = false;

  // Per-card expansion state
  showLine = false;
  showDocs = false;
  showDayType = false;

  // Track approved checks by Name (or other stable id)
  approvedChecks: string[] = [];
  // Currently selected suggestion name (stable key)
  selectedSuggestionName: string | null = null;

  // Quick suggestion prompts
  quickAsks: string[] = [
    'Why did missing docs spike last month?',
    'Top suppliers by weekend spend',
    'Trend drivers for MoM increase',
  ];

  // Small sparkline for header (month-on-month)
  headerSparkData: any[] = [
    { data: [9, 10, 11, 10, 12, 14, 15, 17, 16, 18, 21, 23], label: 'MoM' },
  ];
  headerSparkLabels: string[] = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
  headerSparkOptions: any = { responsive: true, maintainAspectRatio: false, legend: { display: false }, scales: { xAxes: [{ display: false }], yAxes: [{ display: false }] }, elements: { point: { radius: 0 }, line: { tension: 0.35, borderWidth: 2 } } };
  headerSparkColors: any[] = [{ borderColor: '#22c55e', backgroundColor: 'rgba(34,197,94,0.12)' }];
  headerSparkType: any = 'line';

  private detachedToBody = false;
  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private reportingService: UnreconciledReportService,
    private organizationService: OrganizationService,
    @Inject(DOCUMENT) private document: any
  ) { }

  ngOnInit(): void {
    // Only move to body for overlay mode. In dockMode, keep it in place.
    if (!this.dockMode) {
      try {
        if (this.document && this.document.body && this.el && this.el.nativeElement && this.renderer) {
          this.renderer.appendChild(this.document.body, this.el.nativeElement);
          this.detachedToBody = true;
        }
      } catch { /* noop */ }
    }

    this.computeSummary();
    this.seedMessages();
    this.applyStartExpanded();
    this.buildSuggestionItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.status || changes.reportName) {
      this.computeSummary();
      this.seedMessages();
    }
    if (changes.showCharts) {
      this.applyStartExpanded();
    }

    // 🔥 REBUILD SUGGESTIONS WHEN AI RESPONSE CHANGES
    if (changes.aiResponse) {
      this.approvedChecks = [];          // optional: reset approvals
      this.selectedSuggestionName = null; // optional: reset selection
      this.buildSuggestionItems();
    }

    // Start or stop lottie animation when loading flag changes
    if (changes.loading) {
      const isLoading = !!changes.loading.currentValue;
      if (isLoading) {
        // attempt to load lottie dynamically
        this.initLottie();
      } else {
        this.destroyLottie();
      }
    }
  }

  ngOnDestroy(): void {
    try {
      if (!this.dockMode && this.detachedToBody && this.document && this.el && this.el.nativeElement && this.renderer) {
        this.renderer.removeChild(this.document.body, this.el.nativeElement);
      }
    } catch { /* noop */ }
    this.destroyLottie();
  }

  private async initLottie(): Promise<void> {
    // If already loaded, nothing to do
    if (this._lottiePlayer) { return; }
    // if ViewChild not set yet, try again shortly (this happens when component opens quickly)
    if (!this.lottieContainer || !this.lottieContainer.nativeElement) {
      // try again after a short delay
      setTimeout(() => this.initLottie(), 120);
      return;
    }
    try {
      // dynamic import so we don't require lottie-web at build time
      // if the package isn't installed, we gracefully fall back to the static spinner
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const lottie = await import('lottie-web');
      if (lottie && this.lottieContainer && this.lottieContainer.nativeElement) {
        this._lottiePlayer = lottie.loadAnimation({
          container: this.lottieContainer.nativeElement,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          path: '/assets/animations/reporting.json'
        });
        this._lottieLoaded = true;
      }
    } catch (e) {
      // no-op: lottie not available, keep fallback spinner visible
      this._lottieLoaded = false;
    }
  }

  private destroyLottie(): void {
    try {
      if (this._lottiePlayer && this._lottiePlayer.destroy) {
        this._lottiePlayer.destroy();
      }
    } catch { /* noop */ }
    this._lottiePlayer = null;
    this._lottieLoaded = false;
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    this.isOpenChange.emit(this.isOpen);
  }

  openFromBadge(): void {
    this.isOpen = true;
    this.isOpenChange.emit(this.isOpen);
  }

  // Called by child reco-card when user explicitly requests AI call
  emitCallAssistant(): void {
    // Debug: small console trace to confirm event emission during integration testing
    try { console.debug && console.debug('[ai-assistant] emitCallAssistant() called'); } catch (e) { }
    this.callAssistant.emit();
  }

  computeSummary(): void {
    const statusText = this.status ? ` (status: ${this.status})` : '';
    // Compute simple derived metrics from dummy data
    const trend = this.lineChartData?.[0]?.data || [];
    const last = trend[trend.length - 1];
    const prev = trend[trend.length - 2] || last;
    const mom = prev ? Math.round(((last - prev) / prev) * 100) : 0;

    const withDocs = this.docsChartData?.[0] || 0;
    const withoutDocs = this.docsChartData?.[1] || 0;
    const totalDocs = withDocs + withoutDocs;
    const missingPct = totalDocs ? Math.round((withoutDocs / totalDocs) * 100) : 0;

    const dayData = this.weekdayVsWeekendData?.[0]?.data || [0, 0];
    const dayTotal = (dayData[0] || 0) + (dayData[1] || 0);
    const weekdayPct = dayTotal ? Math.round(((dayData[0] || 0) / dayTotal) * 100) : 0;

    this.summaryText = `Here’s a quick take on ${this.reportName}${statusText}: volume is trending ${mom >= 0 ? 'up' : 'down'} ${Math.abs(mom)}% vs last month, missing documents are limited but noteworthy, and weekday spend dominates.`;
    this.highlights = [
      `Volume ${mom >= 0 ? 'up' : 'down'} ${Math.abs(mom)}% MoM`,
      `${missingPct}% of transactions missing docs`,
      `${weekdayPct}% of spend occurs on weekdays`
    ];
  }

  private seedMessages(): void {
    // Start chat empty; summary is shown in the dedicated top summary section
    this.messages = [];
  }

  sendMessage(): void {
    const text = (this.messageText || '').trim();
    if (!text) { return; }
    this.messages.push({ from: 'user', text });
    // Dummy response
    setTimeout(() => {
      this.messages.push({ from: 'bot', text: 'Thanks — I’ll unpack that and surface deeper insights in the next version.' });
    }, 200);
    this.messageText = '';
  }

  selectQuickAsk(q: string): void {
    this.messageText = q;
    this.sendMessage();
    this.showMoreInsights = true;
  }

  private applyStartExpanded(): void {
    if (this.showCharts) {
      this.showLine = this.showDocs = this.showDayType = true;
      this.showMoreInsights = true;
    }
  }

  toggleCard(kind: 'line' | 'docs' | 'day'): void {
    switch (kind) {
      case 'line': this.showLine = !this.showLine; break;
      case 'docs': this.showDocs = !this.showDocs; break;
      case 'day': this.showDayType = !this.showDayType; break;
    }
    if (this.anyExpanded) { this.showMoreInsights = true; }
  }

  expandAllCharts(): void {
    this.showLine = this.showDocs = this.showDayType = true;
    this.showMoreInsights = true;
  }

  get anyExpanded(): boolean {
    return this.showLine || this.showDocs || this.showDayType;
  }

  get statusClass(): string {
    const s = (this.status || '').toLowerCase();
    if (!s) { return 'pill--neutral'; }
    if (s.includes('pending') || s.includes('draft') || s.includes('unassigned')) return 'pill--warning';
    if (s.includes('await')) return 'pill--info';
    if (s.includes('approved') || s.includes('complete')) return 'pill--success';
    return 'pill--neutral';
  }

  get primaryHighlight(): string {
    return (this.aiResponse && this.aiResponse.summary) ? this.aiResponse.summary : ((this.highlights && this.highlights.length > 0) ? this.highlights[0] : 'New recommendation available');
  }

  /**
   * Trim text at the first occurrence of '###' so we only show pre-structured prose.
   */
  public truncateBeforeHash(text?: string | null): string {
    if (!text) return '';
    const idx = text.indexOf('###');
    if (idx === -1) return text;
    return text.substring(0, idx).trim();
  }

  suggestionItems: Array<{
    title?: string;
    name?: string;
    checkedText?: string;
    emoji?: string;
    failedIds?: string[];
    id?: string;
    reviewStatus?: boolean;
  }> = [];


  /**
   * Return an array of suggestion objects sourced from the AI response.
   * Each item has shape { title: string, emoji?: string } so the UI can render an API-provided icon.
   * Priority: aiResponse.highlights -> aiResponse.suggestions -> aiResponse.checksResults (Message/Name)
   */
  // get suggestionItems(): Array<{ title: string; emoji?: string }> {
  //   if (!this.aiResponse) return [];

  //   // Helpers to normalize different possible item shapes
  //   const normalize = (item: any): { title?: string; name?: string; checkedText?: string; emoji?: string; failedIds?: string[], id?: string, reviewStatus?: boolean } | null => {
  //     if (!item) return null;
  //     if (typeof item === 'string') return { title: item };
  //     // If the API returns an object, try common keys for title/text and emoji/icon
  //     const title = item.title || item.text || item.Message || item.message || '';
  //     const name = item.Name || item.name || '';
  //     const checkedText = item.CheckedText || item.checkedText || '';
  //     const emoji = item.emoji || item.icon || item.symbol || item.Emoji;
  //     const failedIds = item.FailedIds || item.failedIds || item.failedIds || [];
  //     const id = item.id || item.ID || item.Id || '';
  //     const reviewStatus = item.reviewStatus || item.ReviewStatus || '';
  //     if (title || name || id) return { title, name, checkedText, emoji, failedIds, id, reviewStatus };
  //     return null;
  //   };

  //   if (Array.isArray(this.aiResponse.highlights) && this.aiResponse.highlights.length) {
  //     // If highlights are present but don't include name/checkedText, try to enrich
  //     // each highlight from checksResults when possible (by index or title match).
  //     const checks = Array.isArray(this.aiResponse.checksResults) ? this.aiResponse.checksResults : [];
  //     const rawItems = this.aiResponse.highlights.map((h: any, idx: number) => {
  //       const base = normalize(h) || {};
  //       if (h.reviewStatus && this.isApprovalMode && !this.approvedChecks.includes(h.id))
  //         this.approvedChecks.push(h.id);

  //       // If base already contains name/checkedText, prefer it
  //       if (base.name && base.name.length) return base;
  //       // Try to enrich using checksResults by index
  //       const byIndex = checks[idx];
  //       if (byIndex) {
  //         const enriched = { ...base };
  //         enriched.name = enriched.name || (byIndex.Name || byIndex.name || '');
  //         enriched.checkedText = enriched.checkedText || (byIndex.CheckedText || byIndex.checkedText || '');
  //         enriched.failedIds = enriched.failedIds && enriched.failedIds.length ? enriched.failedIds : (byIndex.FailedIds || byIndex.failedIds || []);
  //         enriched.emoji = enriched.emoji || (byIndex.Emoji || byIndex.emoji || '');
  //         return enriched;
  //       }
  //       // As a fallback, attempt to find a checksResults item with matching Message/title text
  //       const titleText = (base.title || '').trim();
  //       if (titleText) {
  //         const match = checks.find((c: any) => {
  //           const msg = (c.Message || c.message || '') + '';
  //           const name = (c.Name || c.name || '') + '';
  //           return msg.includes(titleText) || name === titleText;
  //         });
  //         if (match) {
  //           const enriched = { ...base };
  //           enriched.name = enriched.name || (match.Name || match.name || '');
  //           enriched.checkedText = enriched.checkedText || (match.CheckedText || match.checkedText || '');
  //           enriched.failedIds = enriched.failedIds && enriched.failedIds.length ? enriched.failedIds : (match.FailedIds || match.failedIds || []);
  //           enriched.emoji = enriched.emoji || (match.Emoji || match.emoji || '');
  //           return enriched;
  //         }
  //       }
  //       return base;
  //     }).filter((v: any) => !!v) as any;

  //     // filter out AI summary-like entries (skip the summary card that often appears first)
  //     const isSummary = (it: any) => {
  //       const n = (it.name || '').toString().toLowerCase().trim();
  //       const t = (it.title || '').toString().toLowerCase().trim();
  //       return n === 'ai_summary' || n === 'ai summary' || t.includes('ai summary') || t === 'ai-summary' || t === 'ai_summary';
  //     };
  //     const filtered = rawItems.filter((it: any) => !isSummary(it));

  //     // If highlights are unexpectedly short (e.g. only 2 items) but checksResults
  //     // contains more non-summary checks, prefer rendering checksResults (skip first summary item)
  //     const nonSummaryChecksCount = checks.filter((c: any) => {
  //       const n = ((c.Name || c.name || '') + '').toLowerCase();
  //       return n !== 'ai_summary' && n !== 'ai summary';
  //     }).length;

  //     if (filtered.length < nonSummaryChecksCount && nonSummaryChecksCount > 0) {
  //       // build items from checksResults (exclude summary entries)
  //       const checksItems = checks.map((c: any) => normalize(c)).filter((v: any) => !!v && !isSummary(v)) as any;
  //       return checksItems;
  //     }

  //     return filtered;
  //   }

  //   if (Array.isArray(this.aiResponse.suggestions) && this.aiResponse.suggestions.length) {
  //     const items = this.aiResponse.suggestions.map((s: any) => normalize(s)).filter((v: any) => !!v) as any;
  //     // filter out AI summary-like entries
  //     const isSummary = (it: any) => {
  //       const n = (it.name || '').toString().toLowerCase().trim();
  //       const t = (it.title || '').toString().toLowerCase().trim();
  //       return n === 'ai_summary' || n === 'ai summary' || t.includes('ai summary') || t === 'ai-summary' || t === 'ai_summary';
  //     };
  //     return items.filter((it: any) => !isSummary(it));
  //   }

  //   if (Array.isArray(this.aiResponse.checksResults) && this.aiResponse.checksResults.length) {
  //     // Pass the full checksResults object to the normalizer so fields like Emoji are preserved
  //     const items = this.aiResponse.checksResults.map((c: any) => normalize(c)).filter((v: any) => !!v) as any;
  //     const isSummary = (it: any) => {
  //       const n = (it.name || '').toString().toLowerCase().trim();
  //       const t = (it.title || '').toString().toLowerCase().trim();
  //       return n === 'ai_summary' || n === 'ai summary' || t.includes('ai summary') || t === 'ai-summary' || t === 'ai_summary';
  //     };
  //     return items.filter((it: any) => !isSummary(it));
  //   }

  //   return [];
  // }

  buildSuggestionItems() {
    if (!this.aiResponse) {
      this.suggestionItems = [];
      return;
    }

    const normalize = (item: any) => {
      if (!item) return null;
      if (typeof item === 'string') return { title: item };

      const title = item.title || item.text || item.Message || item.message || '';
      const name = item.Name || item.name || '';
      const checkedText = item.CheckedText || item.checkedText || '';
      const emoji = item.emoji || item.icon || item.symbol || item.Emoji;
      const failedIds = item.FailedIds || item.failedIds || [];
      const id = item.id || item.ID || item.Id || '';
      const reviewStatus = item.reviewStatus || item.ReviewStatus || false;

      if (title || name || id) {
        return { title, name, checkedText, emoji, failedIds, id, reviewStatus };
      }
      return null;
    };

    const isSummary = (it: any) => {
      const n = (it.name || '').toLowerCase().trim();
      const t = (it.title || '').toLowerCase().trim();
      return n === 'ai_summary' || n === 'ai summary' ||
        t.includes('ai summary') || t === 'ai-summary' || t === 'ai_summary';
    };

    let items: any[] = [];

    if (Array.isArray(this.aiResponse.highlights) && this.aiResponse.highlights.length) {
      const checks = Array.isArray(this.aiResponse.checksResults) ? this.aiResponse.checksResults : [];

      items = this.aiResponse.highlights.map((h: any, idx: number) => {
        const base: any = normalize(h) || {};

        if (h.reviewStatus && this.isApprovalMode && !this.approvedChecks.includes(h.id)) {
          this.approvedChecks.push(h.id);
        }

        if (base.name && base.name.length) return base;

        const byIndex = checks[idx];
        if (byIndex) {
          return {
            ...base,
            name: base.name || (byIndex.Name || byIndex.name || ''),
            checkedText: base.checkedText || (byIndex.CheckedText || byIndex.checkedText || ''),
            failedIds: base.failedIds?.length ? base.failedIds : (byIndex.FailedIds || byIndex.failedIds || []),
            emoji: base.emoji || (byIndex.Emoji || byIndex.emoji || '')
          };
        }

        return base;
      }).filter(v => !!v && !isSummary(v));
    }

    if (!items.length && Array.isArray(this.aiResponse.suggestions)) {
      items = this.aiResponse.suggestions
        .map((s: any) => normalize(s))
        .filter(v => !!v && !isSummary(v));
    }

    if (!items.length && Array.isArray(this.aiResponse.checksResults)) {
      items = this.aiResponse.checksResults
        .map((c: any) => normalize(c))
        .filter(v => !!v && !isSummary(v));
    }

    this.suggestionItems = items;
  }


  /**
   * Return checksResults array from the AI payload, or empty array.
   */
  get checksResults(): any[] {
    if (this.aiResponse && Array.isArray(this.aiResponse.checksResults)) return this.aiResponse.checksResults;
    return [];
  }

  // User clicked a suggestion card: select it and emit the failedIds for parent to handle highlighting
  onSelectSuggestion(suggestion: any): void {
    if (!suggestion) return;
    const name = (suggestion.id || '') + '';
    this.selectedSuggestionName = name || null;
    const ids = suggestion.failedIds || suggestion.FailedIds || suggestion.failedIds || [];
    try { this.suggestionSelected.emit(Array.isArray(ids) ? ids : []); } catch (e) { /* noop */ }
  }

  // Toggle approval state for a suggestion/check
  toggleApprove(suggestion: any, e?: Event): void {
    try { if (e) { e.stopPropagation(); e.preventDefault(); } } catch { }
    if (!suggestion) return;
    const id = (suggestion.id);
    if (this.approvedChecks.includes(id)) {
      this.approvedChecks = this.approvedChecks.filter(x => x !== suggestion.id);
      this.aiResponse.highlights.find((h: any) => h.id === id).reviewStatus = false;
      // this.reportingService.UpdateApprovalStatus(id, false, this.organizationService.selectedOrganization).subscribe({
      //   next: (res) => {
      //   }
      // });
    }
    else {
      this.approvedChecks.push(id);
      this.aiResponse.highlights.find((h: any) => h.id === id).reviewStatus = true;
      // this.reportingService.UpdateApprovalStatus(id, true, this.organizationService.selectedOrganization).subscribe({
      //   next: (res) => {
      //   }
      // });
    }
  }

  // Close/dismiss the promo (toast/card). If called from the toast wrapper, prevent click propagation
  closePromo(e?: Event): void {
    try { if (e) { e.stopPropagation(); e.preventDefault(); } } catch { }
    this.promoDismissed = true;
  }

  openApproveModal(): void {
    for (let i = 0; i < this.approvedChecks.length; i++) {
      this.reportingService.UpdateApprovalStatus(this.approvedChecks[i], true, this.organizationService.selectedOrganization).subscribe({
        next: (res) => {
        }
      });
    }
    this.openApproverModalEvt.emit();
  }
  isApproved(id: string): boolean {
    return this.approvedChecks.includes(id);
  }
}